<template>
  <div ref="chartRef" class="chart"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import * as echarts from 'echarts';

const chartRef = ref(null);
let chart = null;
let timer = null;
let currentIndex = 0;

const years = [
  '2010',
  '2011',
  '2012',
  '2013',
  '2014',
  '2015',
  '2016',
  '2017',
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
  '2027',
  '2028',
  '2029',
  '2030',
];

const populationData = [
  22935, 21284, 23263, 22163, 22896, 20043, 24716, 30536, 25829, 24013, 21470, 18458, 16691, 16281,
  16799, 16463, 16133, 15810, 15493, 15183, 14879, 16799,
];

const initChart = () => {
  chart = echarts.init(chartRef.value);

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: '#5680f9',
      textStyle: {
        color: '#fff',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: years,
      axisLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
      axisLabel: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 10,
        interval: 0,
        rotate: 45,
      },
    },
    yAxis: {
      type: 'value',
      name: '出生人口',
      nameTextStyle: {
        color: 'rgba(255, 255, 255, 0.8)',
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      axisLabel: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 10,
      },
    },
    series: [
      {
        name: '出生人口',
        type: 'bar',
        data: populationData.map((value, index) => ({
          value: value,
          itemStyle: {
            color:
              index === 0
                ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#ffd700' },
                    { offset: 0.5, color: '#ffa500' },
                    { offset: 1, color: '#ff8c00' },
                  ])
                : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#83bff6' },
                    { offset: 0.5, color: '#188df0' },
                    { offset: 1, color: '#188df0' },
                  ]),
          },
          label: {
            show: index === 0,
            position: 'top',
            color: '#fff',
            fontSize: 12,
            fontWeight: 'bold',
            formatter: '{c}',
            backgroundColor: 'rgba(255, 165, 0, 0.8)',
            borderColor: '#ff8c00',
            borderWidth: 1,
            borderRadius: 4,
            padding: [4, 8],
            shadowBlur: 10,
            shadowColor: 'rgba(255, 165, 0, 0.5)',
          },
        })),
        emphasis: {
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#2378f7' },
              { offset: 0.7, color: '#2378f7' },
              { offset: 1, color: '#83bff6' },
            ]),
          },
        },
      },
    ],
  };

  chart.setOption(option);
};

// 开始循环显示标签
const startLabelAnimation = () => {
  if (timer) clearInterval(timer);

  timer = setInterval(() => {
    // 重置所有柱子的样式
    const resetData = populationData.map((value) => ({
      value: value,
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#83bff6' },
          { offset: 0.5, color: '#188df0' },
          { offset: 1, color: '#188df0' },
        ]),
      },
      label: {
        show: false,
      },
    }));

    // 高亮当前年份
    resetData[currentIndex] = {
      value: populationData[currentIndex],
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#ffd700' },
          { offset: 0.5, color: '#ffa500' },
          { offset: 1, color: '#ff8c00' },
        ]),
      },
      label: {
        show: true,
        position: 'top',
        color: '#fff',
        fontSize: 12,
        fontWeight: 'bold',
        formatter: '{c}',
        backgroundColor: 'rgba(255, 165, 0, 0.8)',
        borderColor: '#ff8c00',
        borderWidth: 1,
        borderRadius: 4,
        padding: [4, 8],
        shadowBlur: 10,
        shadowColor: 'rgba(255, 165, 0, 0.5)',
      },
    };

    // 更新图表
    chart.setOption({
      series: [
        {
          data: resetData,
        },
      ],
    });

    // 移动到下一个年份
    currentIndex = (currentIndex + 1) % years.length;
  }, 1500); // 每1.5秒切换一次
};

// 停止动画
const stopLabelAnimation = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

onMounted(() => {
  initChart();
  startLabelAnimation();
  window.addEventListener('resize', () => chart?.resize());
});

onUnmounted(() => {
  stopLabelAnimation();
  if (chart) {
    chart.dispose();
    chart = null;
  }
});
</script>

<style scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
